import { useState, useCallback } from 'react';
import {
    CreateUserAdminUserPostData,
    createUserAdminUserPost,
    UpdateUserAdminUserUserIdPatchData,
    updateUserAdminUserUserIdPatch,
    listUsersAdminUsersGet,
    ListUsersAdminUsersGetData,
    getUserAdminUsersUserIdGet,
    GetUserAdminUsersUserIdGetData,
    updatePasswordAdminUsersUserIdPasswordPatch,
    UpdatePasswordAdminUsersUserIdPasswordPatchData,
    DeleteUserAdminUserUserIdDeleteData,
    deleteUserAdminUserUserIdDelete,
    restoreUserAdminUsersUserIdRestorePost,
    RestoreUserAdminUsersUserIdRestorePostData,
    permanentlyDeleteUserAdminUsersUserIdPermanentDelete,
    PermanentlyDeleteUserAdminUsersUserIdPermanentDeleteData
} from "../client";

export const useUser = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const createUserAdmin = useCallback(async (data: CreateUserAdminUserPostData) => {
        setLoading(true);
        setError(null);
        try {
            const response = await createUserAdminUserPost(data);
            return response;
        } catch (error: any) {
            console.log('Error full object:', error);

            // ApiErrorの場合
            if (error.name === 'ApiError' && error.body) {
                throw {
                    type: error.body.type,
                    message: error.body.message,
                    details: error.body.details,
                    status: error.status
                };
            }

            // その他のエラー
            throw {
                type: 'UnknownError',
                message: error.message || '予期せぬエラーが発生しました',
                status: 500
            };
        } finally {
            setLoading(false);
        }
    }, []);

    const updatePassword = useCallback(async (data: UpdatePasswordAdminUsersUserIdPasswordPatchData) => {
        setLoading(true);
        setError(null);
        try {
            const response = await updatePasswordAdminUsersUserIdPasswordPatch(data);
            return response;
        } catch (error: any) {
            setError(error.message);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const updateUser = useCallback(async (data: UpdateUserAdminUserUserIdPatchData) => {
        setLoading(true);
        setError(null);
        try {
            const response = await updateUserAdminUserUserIdPatch(data);
            return response;
        } catch (error: any) {
            console.log('Error full object:', error);

            // ApiErrorの場合
            if (error.name === 'ApiError' && error.body) {
                throw {
                    type: error.body.type,
                    message: error.body.message,
                    details: error.body.details,
                    status: error.status
                };
            }

            // その他のエラー
            throw {
                type: 'UnknownError',
                message: error.message || '予期せぬエラーが発生しました',
                status: 500
            };
        } finally {
            setLoading(false);
        }
    }, []);

    const deleteUser = useCallback(async (data: DeleteUserAdminUserUserIdDeleteData) => {
        setLoading(true);
        setError(null);
        try {
            const response = await deleteUserAdminUserUserIdDelete(data);
            return response.message;
        } catch (error: any) {
            if (error.response?.data) {
                const errorData = error.response.data;
                throw {
                    type: errorData.type,
                    message: errorData.message || '削除に失敗しました',
                    status: error.response.status
                };
            }
            throw {
                type: 'UnknownError',
                message: error.message || '予期せぬエラーが発生しました',
                status: 500
            };
        } finally {
            setLoading(false);
        }
    }, []);

    const restoreUser = useCallback(async (data: RestoreUserAdminUsersUserIdRestorePostData) => {
        setLoading(true);
        setError(null);
        try {
            const response = await restoreUserAdminUsersUserIdRestorePost(data);
            return response;
        } catch (error: any) {
            if (error.response?.data) {
                const errorData = error.response.data;
                throw {
                    type: errorData.type,
                    message: errorData.message || '復元に失敗しました',
                    status: error.response.status
                };
            }
            throw {
                type: 'UnknownError',
                message: error.message || '予期せぬエラーが発生しました',
                status: 500
            };
        } finally {
            setLoading(false);
        }
    }, []);

    const permanentlyDeleteUser = useCallback(async (data: PermanentlyDeleteUserAdminUsersUserIdPermanentDeleteData) => {
        setLoading(true);
        setError(null);
        try {
            await permanentlyDeleteUserAdminUsersUserIdPermanentDelete(data);
            return "ユーザーを完全に削除しました";
        } catch (error: any) {
            if (error.response?.data) {
                const errorData = error.response.data;
                throw {
                    type: errorData.type,
                    message: errorData.message || '完全削除に失敗しました',
                    status: error.response.status
                };
            }
            throw {
                type: 'UnknownError',
                message: error.message || '予期せぬエラーが発生しました',
                status: 500
            };
        } finally {
            setLoading(false);
        }
    }, []);

    const getUserList = useCallback(async (data: ListUsersAdminUsersGetData) => {
        setLoading(true);
        setError(null);
        try {
            const response = await listUsersAdminUsersGet(data);
            return response;
        } catch (error: any) {
            setError(error.message);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const getUser = useCallback(async (data: GetUserAdminUsersUserIdGetData) => {
        setLoading(true);
        setError(null);
        try {
            const response = await getUserAdminUsersUserIdGet(data);
            return response;
        } catch (error: any) {
            setError(error.message);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        loading,
        error,
        createUserAdmin,
        deleteUser,
        restoreUser,
        permanentlyDeleteUser,
        updateUser,
        getUserList,
        getUser,
        updatePassword
    };
};

export default useUser;